import React, { useState, useCallback, useMemo } from "react";
import ChartModal from "../../../ui/ChartModal";
import ChartControls from "../controls/ChartControls";
import filterStyles from "../../../filters/FilterPanel.module.css";
import layoutStyles from "../../../../styles/utilities/layout.module.css";
import { exportChartData } from "../../../../lib/utils/export-chart";
import type { ExportFormat } from "../../../../lib/utils/export-chart";

type ChartMode = string;

interface BaseExpandableChartProps {
  data: any[];
  previewData?: any[];
  ChartComponent: React.ComponentType<any>;
  ExpandedChartComponent?: React.ComponentType<any>;
  supportsTotal?: boolean;
  supportsSingleMode?: boolean;
  initialLeftMode?: ChartMode;
  initialRightMode?: ChartMode;
  initialSingleMode?: ChartMode;
  initialShowTotal?: boolean;
  onExport?: (format: string, expanded?: string | null) => void;
  onDataProcess?: (data: any[]) => any[];
  chartProps?: Record<string, any>;
  children?: React.ReactNode;
  isDualChart?: boolean;
  chartTitle?: string;
}

interface ChartState {
  expanded: string | null;
  leftMode: ChartMode;
  rightMode: ChartMode;
  singleMode: ChartMode;
  showTotal: boolean;
  modalMode: ChartMode;
  modalShowTotal: boolean;
}

const BaseExpandableChart: React.FC<BaseExpandableChartProps> = ({
  data,
  previewData,
  ChartComponent,
  ExpandedChartComponent,
  supportsTotal = false,
  supportsSingleMode = true,
  initialLeftMode = "line",
  initialRightMode = "line",
  initialSingleMode = "line",
  initialShowTotal = false,
  onExport,
  onDataProcess,
  chartProps = {},
  children,
  isDualChart = false,
  chartTitle = "Chart",
}) => {
  const [chartState, setChartState] = useState<ChartState>({
    expanded: null,
    leftMode: initialLeftMode,
    rightMode: initialRightMode,
    singleMode: initialSingleMode,
    showTotal: initialShowTotal,
    modalMode: "line",
    modalShowTotal: false,
  });

  const updateChartState = useCallback((updates: Partial<ChartState>) => {
    setChartState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleExpand = useCallback(
    (chartType: string = "volume") => {
      if (window.innerWidth <= 768) {
        return;
      }
      updateChartState({ expanded: chartType });
    },
    [updateChartState]
  );

  const handleModalClose = useCallback(() => {
    updateChartState({ expanded: null });
  }, [updateChartState]);

  const handleModalModeChange = useCallback(
    (mode: ChartMode) => {
      updateChartState({ modalMode: mode });
    },
    [updateChartState]
  );

  const handleModalShowTotalChange = useCallback(
    (show: boolean) => {
      updateChartState({ modalShowTotal: show });
    },
    [updateChartState]
  );

  const processedData = useMemo(
    () => (onDataProcess ? onDataProcess(data) : data),
    [data, onDataProcess]
  );

  const processedPreviewData = useMemo(
    () => previewData || processedData,
    [previewData, processedData]
  );

  const defaultExport = useCallback(
    (format: string, expanded?: string | null) => {
      if (!processedData || !processedData.length) return;

      const metricFromExpanded = expanded || chartState.expanded || chartState.modalMode;
      const metricType =
        metricFromExpanded === "volume"
          ? "volume"
          : metricFromExpanded === "count"
            ? "count"
            : null;

      exportChartData(format as ExportFormat, {
        data: processedData,
        chartTitle,
        metricType: metricType as "volume" | "count" | null,
        includeTotal: chartState.modalShowTotal,
      });
    },
    [
      processedData,
      chartState.modalShowTotal,
      chartState.expanded,
      chartState.modalMode,
      chartTitle,
    ]
  );

  const handleExport = useCallback(
    (format: string) => {
      if (onExport) {
        onExport(format, chartState.expanded);
      } else {
        defaultExport(format, chartState.expanded);
      }
    },
    [onExport, defaultExport, chartState.expanded]
  );

  const baseChartProps = useMemo(() => {
    return {
      data: processedPreviewData,
      leftMode: chartState.leftMode,
      rightMode: chartState.rightMode,
      singleMode: chartState.singleMode,
      showTotal: chartState.showTotal,
      onExpand: handleExpand,
      onExport: handleExport,
      ...chartProps,
      children: chartProps.children ?? null,
    };
  }, [
    processedPreviewData,
    chartState.leftMode,
    chartState.rightMode,
    chartState.singleMode,
    chartState.showTotal,
    handleExpand,
    handleExport,
    chartProps,
  ]);

  const expandedChartProps = useMemo(
    () => ({
      data: processedData,
      mode: chartState.modalMode,
      showTotal: chartState.modalShowTotal,
      isExpanded: true,
      expandedChart: chartState.expanded,
      controls: (
        <ChartControls
          isDualChart={false}
          controlsGrid={true}
          showModeControls={supportsSingleMode}
          leftMode={chartState.leftMode}
          rightMode={chartState.rightMode}
          singleMode={chartState.modalMode}
          onLeftModeChange={(mode: ChartMode) => updateChartState({ leftMode: mode })}
          onRightModeChange={(mode: ChartMode) => updateChartState({ rightMode: mode })}
          onSingleModeChange={handleModalModeChange}
          showTotalControl={false}
          showTotal={false}
          onShowTotalChange={() => undefined}
          showExpandButton={false}
          showExportButton={false}
          onExport={() => undefined}
          onExpand={() => undefined}
          children={null}
        />
      ),
      ...chartProps,
    }),
    [
      processedData,
      chartState,
      chartProps,
      handleModalModeChange,
      supportsSingleMode,
      updateChartState,
    ]
  );

  const modalTitle = useMemo(() => {
    if (chartState.expanded === "volume") return "Invested capital";

    const typeLabels: Record<string, string> = {
      volume: "Invested capital",
      count: "Number of deals",
    };

    if (chartState.expanded === "count") return typeLabels.count;

    return `Expanded ${typeLabels[chartState.expanded || ""] || "Chart"}`;
  }, [chartState.expanded]);

  return (
    <div>
      <ChartComponent {...baseChartProps} />

      {children}

      <ChartModal
        isOpen={chartState.expanded !== null}
        onClose={handleModalClose}
        title={modalTitle}
        subtitle={chartState.expanded === "volume" ? "in CHF Mio." : ""}
        onExport={handleExport}
        headerRight={
          supportsTotal ? (
            <label
              className={`${layoutStyles.flexRow} ${layoutStyles.itemsCenter} ${layoutStyles.gap2} ${filterStyles.showTotalLabelWrapper}`}
              style={{
                paddingLeft: "0.75rem",
                paddingRight: "0.75rem",
                height: "2.25rem",
                whiteSpace: "nowrap",
              }}
            >
              <input
                type="checkbox"
                checked={chartState.modalShowTotal}
                onChange={(e) => handleModalShowTotalChange(e.target.checked)}
                className={filterStyles.checkbox}
              />
              <span className={filterStyles.showTotalLabel}>Show total</span>
            </label>
          ) : null
        }
      >
        {chartState.expanded && (
          <div>
            {ExpandedChartComponent ? (
              <div className={`${layoutStyles.hFull} ${layoutStyles.minHeight0}`}>
                <ExpandedChartComponent {...expandedChartProps} />
              </div>
            ) : (
              <div className={`${layoutStyles.hFull} ${layoutStyles.minHeight0}`}>
                <div
                  className={`${layoutStyles.minHeight0} ${layoutStyles.hFull}`}
                  style={{ minWidth: 0 }}
                >
                  <ChartComponent {...expandedChartProps} />
                </div>
              </div>
            )}
          </div>
        )}
      </ChartModal>
    </div>
  );
};

BaseExpandableChart.displayName = "BaseExpandableChart";

export default BaseExpandableChart;
